import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { alCaducarSesion, borrarSesion, guardarSesion, haySesion, tokenRefrescoActual } from '../api/cliente'
import { autenticacion, configuracion as apiConfiguracion } from '../api/servicios'
import { olvidarCatalogo } from '../api/catalogoLocal'
import { fijarMonedaNegocio } from '../api/moneda'
import { fijarZonaNegocio } from '../api/zona'
import type { UsuarioAutenticado } from '../api/tipos'

interface ValorAuth {
    usuario: UsuarioAutenticado | null
    cargando: boolean
    /** Cambia cuando se adopta la zona del negocio, para que todo se vuelva a pintar. */
    zonaLista: boolean
    entrar: (nombreUsuario: string, contrasena: string) => Promise<void>
    salir: () => Promise<void>
    /** Pasa a operar en otro local. Reemplaza la sesion entera. */
    cambiarSucursal: (sucursalId: number) => Promise<void>
    puede: (permiso: string) => boolean
}

const Contexto = createContext<ValorAuth | null>(null)

export function ProveedorAuth({ children }: { children: ReactNode }) {
    const [usuario, setUsuario] = useState<UsuarioAutenticado | null>(null)
    const [cargando, setCargando] = useState(true)
    const [zonaLista, setZonaLista] = useState(false)

    /**
     * Adopta la zona horaria del negocio. Se hace una vez por sesion y no en
     * cada pantalla: la zona decide que dia es cada instante, asi que si dos
     * pantallas la tomaran en momentos distintos mostrarian fechas distintas
     * para la misma venta.
     *
     * Si falla, se sigue con la de por defecto: no poder leer la configuracion
     * no es motivo para dejar al cajero sin sistema.
     */
    const cargarZona = useCallback(async () => {
        try {
            const datos = await apiConfiguracion.obtener()
            fijarZonaNegocio(datos.zonaHoraria)
            fijarMonedaNegocio(datos)
        } catch {
            // Se queda la de por defecto.
        } finally {
            setZonaLista(true)
        }
    }, [])

    // Al arrancar con una sesion guardada se pregunta quien es: los permisos
    // pudieron cambiar desde el ultimo ingreso y el token viejo los traeria
    // desactualizados.
    useEffect(() => {
        let vigente = true

        async function recuperar() {
            if (!haySesion()) {
                setCargando(false)
                return
            }

            try {
                const actual = await autenticacion.yo()
                if (vigente) setUsuario(actual)
                await cargarZona()
            } catch {
                borrarSesion()
            } finally {
                if (vigente) setCargando(false)
            }
        }

        void recuperar()

        return () => {
            vigente = false
        }
    }, [cargarZona])

    // Si el refresco falla en cualquier peticion, se vuelve al login sin dejar
    // la pantalla a medio cargar.
    useEffect(() => {
        alCaducarSesion(() => setUsuario(null))
    }, [])

    const entrar = useCallback(async (nombreUsuario: string, contrasena: string) => {
        const sesion = await autenticacion.entrar(nombreUsuario, contrasena)
        guardarSesion(sesion)
        setUsuario(sesion.usuario)
        await cargarZona()
    }, [cargarZona])

    /*
     * El backend revoca los tokens anteriores y emite unos nuevos con la
     * sucursal elegida. Por eso se guardan los dos y se reemplaza el usuario
     * entero: quedarse con el token viejo dejaria al usuario vendiendo en el
     * local de antes mientras la pantalla dice otra cosa.
     */
    const cambiarSucursal = useCallback(async (sucursalId: number) => {
        const sesion = await autenticacion.cambiarSucursal(sucursalId)
        guardarSesion(sesion)
        setUsuario(sesion.usuario)
    }, [])

    const salir = useCallback(async () => {
        try {
            await autenticacion.salir(tokenRefrescoActual())
        } catch {
            // Cerrar sesion nunca debe fallar de cara al usuario: si el servidor
            // no responde, igual se limpia el equipo.
        } finally {
            borrarSesion()

            // El catalogo en memoria se olvida: el siguiente turno puede ser de
            // otro local, con otros productos y otros precios.
            olvidarCatalogo()

            setUsuario(null)
        }
    }, [])

    const valor = useMemo<ValorAuth>(
        () => ({
            usuario,
            cargando,
            zonaLista,
            entrar,
            salir,
            cambiarSucursal,
            puede: (permiso: string) => usuario?.permisos.includes(permiso) ?? false,
        }),
        [usuario, cargando, zonaLista, entrar, salir, cambiarSucursal],
    )

    return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useAuth(): ValorAuth {
    const valor = useContext(Contexto)

    if (!valor) {
        throw new Error('useAuth debe usarse dentro de ProveedorAuth.')
    }

    return valor
}
